import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

export interface MeResponse {
  authenticated: boolean;
  user: { id: number; username: string; role: string; mustChangePassword: boolean } | null;
  authenticationMode: 'local' | 'hybrid' | 'zoho';
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly user = signal<MeResponse['user']>(null);
  readonly authenticationMode = signal<MeResponse['authenticationMode']>('local');
  readonly ready = signal(false);

  constructor(private readonly api: ApiService, private readonly router: Router) {}

  async loadMe(): Promise<void> {
    try {
      const me = await firstValueFrom(this.api.get<MeResponse>('/auth/me'));
      this.user.set(me.user);
      this.authenticationMode.set(me.authenticationMode);
    } catch {
      this.user.set(null);
      this.authenticationMode.set('local');
    } finally {
      this.ready.set(true);
    }
  }

  async login(username: string, password: string): Promise<void> {
    await firstValueFrom(this.api.post('/auth/login', { username, password }));
    await this.loadMe();
    window.dispatchEvent(new Event('settings-changed'));
    window.dispatchEvent(new Event('vm-session-changed'));
    if (this.user()?.mustChangePassword) {
      await this.router.navigateByUrl('/account/password?required=1');
      return;
    }
    try {
      const ctx = await firstValueFrom(this.api.get<{ activeProperty: { id: number } | null }>('/context/property'));
      await this.router.navigateByUrl(ctx.activeProperty?.id ? '/reservations' : '/properties');
    } catch {
      await this.router.navigateByUrl('/properties');
    }
  }

  beginZohoLogin(): void {
    window.location.assign('/api/auth/zoho/start');
  }

  async logout(): Promise<void> {
    await firstValueFrom(this.api.post('/auth/logout', {}));
    this.user.set(null);
    window.dispatchEvent(new Event('settings-changed'));
    window.dispatchEvent(new Event('vm-session-changed'));
    await this.router.navigateByUrl('/login');
  }
}
